import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import Checkbox from '@components/custom/Checkbox';
import Icon from '@components/custom/Icon';
import ModuleHeader from '@components/ModuleHeader';
import Pagination from '@components/custom/Pagination';
import ScrollList from '@components/custom/ScrollList';
import Spinner from '@components/custom/Spinner';

import '@assets/css/notifications.css';

import { crudActions } from '@sm/services/crudActions';
import { showAlert } from '@sm/store/alertSlice';

const Notifications = () => {
  const dispatch = useDispatch();

  const perPage = 20;
  const periods = useMemo(() => [{
    label: 'Last Week',
    value: 'LAST_WEEK'
  }, {
    label: 'Unread',
    value: 'UNREAD'
  }, {
    label: 'All',
    value: 'ALL'
  }], []);

  const [isLoading, setIsLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('UNREAD');
  const [page, setPage] = useState(1);
  const [notifications, setNotifications] = useState([]);
  const [cnt, setCnt] = useState(0);
  const [selectedNotifications, setSelectedNotifications] = useState([]);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    let endpoint = `v1/notifications?page=${page}&size=${perPage}`;
    if (selectedPeriod === 'LAST_WEEK') {
      endpoint += `&lastWeek=true`;
    }
    if (selectedPeriod === 'UNREAD') {
      endpoint += `&unread=true`;
    }

    crudActions.get(endpoint, {}, true)
      .then(response => {
        if (isMounted && response) {
          setNotifications(response.data);
          setCnt(response.cnt);
        }
      })
      .catch(err => {
        if (isMounted) {
          dispatch(showAlert('danger', err?.message));
        }
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [dispatch, page, selectedPeriod]);

  useEffect(() => {
    setPage(1);
    setSelectedNotifications([]);
  }, [selectedPeriod]);

  const handleChangePage = (pageNum) => {
    setPage(pageNum);
    setSelectedNotifications([]);
  };

  const onNotificationClick = useCallback((value) => {
    const isChecked = !selectedNotifications.includes(value);

    setSelectedNotifications(prev => {
      const updatedNotifications = isChecked
        ? [...prev, value]
        : prev.filter(item => item !== value);

      return updatedNotifications;
    });
  }, [selectedNotifications]);

  const handleSelectAllClick = () => {
    setSelectedNotifications(
      isAllSelected ? [] : notifications.map(n => n.id)
    );
  };

  const handleReadNotificationsClick = () => {
    if (!selectedNotifications.length) return;

    crudActions.put(`v1/notifications/change-status/list`, {
      ids: selectedNotifications,
      isRead: true
    }, {}, true)
      .then(() => {
        const updatedNotifications = notifications.map(n => ({
          ...n,
          ...(selectedNotifications.includes(n.id) && { isRead: true} ) 
        }));
        setNotifications(updatedNotifications);
        setSelectedNotifications([]);
      })
      .catch(err => {
        dispatch(showAlert('danger', err?.message));
      });
  };

  const handleDeleteNotificationsClick = () => {
    if (!selectedNotifications.length) return;
    
    crudActions.remove(`v1/notifications`, { ids: selectedNotifications }, {}, true)
      .then(() => {
        const updatedNotifications = notifications.filter(n => !selectedNotifications.includes(n.id));
        setNotifications(updatedNotifications);
        setSelectedNotifications([]);
      })
      .catch(err => {
        dispatch(showAlert('danger', err?.message));
      });
  };

  const totalPageCount = Math.ceil(cnt / perPage);
  const isAllSelected = selectedNotifications?.length === notifications?.length && notifications?.length > 0;

  return (
    <div className='col-12 module notifications'>
      <div className='component shadow mb-4 position-relative'>
        <ModuleHeader
          id='notifications'
          title='Notifications'
          actions={
            <ScrollList
              items={ periods }
              selectedItem={ selectedPeriod }
              onClick={ (value) => setSelectedPeriod(value) }
            />
          }
        />

        {isLoading && <Spinner /> }

        {!isLoading && (
          <div className='actions mt-3 ps-2 d-flex'>
            <Icon
              name={ isAllSelected ? 'indeterminate_check_box' : 'check_box_outline_blank' }
              onClick={ () => handleSelectAllClick() }
            />
            <Icon
              name='mark_email_read'
              disabled={ !selectedNotifications.length }
              onClick={ () => handleReadNotificationsClick() }
            />
            <Icon
              name='delete'
              disabled={ !selectedNotifications.length }
              onClick={ () => handleDeleteNotificationsClick() }
            />
          </div>
        )}
        <div className='row mt-2 list'>
          {notifications.map(n => (
            <div key={ n.id } className='col-12'>
              <div
                className={ `item${!n.isRead ? ' unread' : ''}` }
                onClick={ () => onNotificationClick(n.id) }
              >
                <div className='d-flex info'>
                  <div className='checkbox'>
                    <Checkbox
                      id={ n.id }
                      value={ n.id }
                      checked={ selectedNotifications.includes(n.id) }
                      readOnly
                    />
                  </div>
                  <div className='d-flex flex-column'>
                    <span className='title'>{ n.title }</span>
                    <span className='text'>{ n.text }</span>
                  </div>
                </div>
                <div className='d-flex date'>
                  <span> { n.dateTime.split(' ')[0] } </span>
                  <span> { n.dateTime.split(' ')[1] } </span>

                  {!n.isRead && <span className='status' />}
                </div>
              </div>
            </div>
          ))}
        </div>

        {totalPageCount > 0 && (
          <div className="pagination">
            <Pagination
              currentPage={ Number(page) }
              totalPages={ totalPageCount }
              onPageChange={ handleChangePage }
            />
          </div>
        )}

      </div>
    </div>
  );
};

export default Notifications;